import md5 from 'blueimp-md5';
import jwtDecode from 'jwt-decode';
import { history } from 'umi';

import { authLogout, authRefreshToken } from '@/services/login';
import tokenManager from '@/services/token';
import {
  clearRestrictedBillingSession,
  clearUserAuthTokens,
  getCurrentSession,
  getCurrentSessionId,
  getOperationalAuthBlock,
  getSessionItem,
  hasRestrictedBillingToken,
  isOperationalAccessBlockedResponse,
  isOperationalRefreshBlockedResponse,
  removeSessionItem,
  resolveOperationalBlockRoute,
  setAuthority,
  setCurrentSession,
  setGlobalItem,
  setOperationalAuthBlock,
  setRestrictedBillingSession,
  setSessionItem,
  STORAGE_KEYS,
} from '../auth';
import { getToken, isPublicFragment, isTokenExpired, removeTokenById } from './tokenManager';

let notifyContext = null;
export const setNotifyContext = (context) => (notifyContext = context);

// Mutex for preventing concurrent token refresh requests
let refreshPromise = null;
let isRefreshing = false;
let operationalBlockHandling = false;

export const REFRESH_RESULT = Object.freeze({
  SUCCESS: true,
  FAILED: false,
  OPERATIONAL_BLOCK: 'operational_block',
});

/**
 * Clear normal access/refresh tokens without redirecting to login.
 * Preserves sessionStorage entries needed for the operational block screen.
 */
const clearNormalAuthTokens = () => {
  const userId = getCurrentSession()?.userId;
  removeSessionItem(STORAGE_KEYS.AUTH_TOKEN);
  removeSessionItem(STORAGE_KEYS.AUTHORITY);
  removeSessionItem(STORAGE_KEYS.AUTH_USER);
  removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
  removeSessionItem(STORAGE_KEYS.AUTH_TOKEN_REFRESH);
  removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
  clearUserAuthTokens(userId);
  setCurrentSession(null);
};

const extractResponseBody = async (responseOrError) => {
  if (!responseOrError) return null;

  if (isOperationalAccessBlockedResponse(responseOrError)) {
    return responseOrError;
  }

  if (responseOrError.authResult) return responseOrError;

  const response = responseOrError.response || responseOrError;
  if (typeof response?.clone === 'function') {
    try {
      return await response.clone().json();
    } catch (e) {
      return null;
    }
  }

  if (responseOrError.data && typeof responseOrError.data === 'object') {
    return responseOrError.data;
  }

  return null;
};

/**
 * Handle auth 403 with authResult.credentialsValid = true (login or refresh).
 * Never opens the main shell. Restricted token (login only) stays in memory.
 *
 * @param {object} responseBody
 * @param {{ fromRefresh?: boolean }} [options]
 * @returns {Promise<typeof REFRESH_RESULT.OPERATIONAL_BLOCK>}
 */
export const handleOperationalAccessBlock = async (responseBody, options = {}) => {
  if (operationalBlockHandling) return REFRESH_RESULT.OPERATIONAL_BLOCK;
  operationalBlockHandling = true;

  try {
    if (options.fromRefresh) {
      isRefreshing = false;
      refreshPromise = null;
      removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
      window.dispatch?.({ type: 'global/resetRefreshToken' });

      const { clearRequestQueue } = require('./requestQueue');
      clearRequestQueue(responseBody);
      // Refresh never emits restrictedToken — clear any stale recovery session.
      clearRestrictedBillingSession();
    }

    clearNormalAuthTokens();
    setOperationalAuthBlock(responseBody);

    const withRestricted = !options.fromRefresh && hasRestrictedBillingToken(responseBody);
    if (withRestricted) {
      setRestrictedBillingSession(responseBody);
    } else if (!options.fromRefresh) {
      clearRestrictedBillingSession();
    }

    const reduxPayload = {
      authResult: responseBody.authResult,
      code: responseBody.code,
      message: responseBody.message,
      status: responseBody.status,
      hasRestrictedToken: withRestricted,
    };

    try {
      window.dispatch?.({ type: 'auth/setOperationalBlock', payload: reduxPayload });
    } catch (e) {}

    try {
      const appInsightsService = require('@/utils/appInsights').default;
      const authResult = responseBody?.authResult || {};
      appInsightsService.trackEvent('OperationalAccessBlocked', {
        code: responseBody?.code,
        accessMode: authResult.accessMode,
        blockReason: authResult.blockReason,
        allowedActions: Array.isArray(authResult.allowedActions)
          ? authResult.allowedActions.join(',')
          : '',
        hasRestrictedToken: withRestricted,
        source: options.fromRefresh ? 'refresh' : options.source || 'login',
        surface: options.surface || (options.fromRefresh ? 'refresh-token' : 'main-app'),
      });
    } catch (e) {}

    const route = resolveOperationalBlockRoute(responseBody, {
      hasRestrictedToken: withRestricted,
    });
    const currentPath = history.location?.pathname;
    if (currentPath !== route) {
      history.replace(route);
    }

    return REFRESH_RESULT.OPERATIONAL_BLOCK;
  } finally {
    setTimeout(() => {
      operationalBlockHandling = false;
    }, 1500);
  }
};

/** @deprecated Prefer handleOperationalAccessBlock({ fromRefresh: true }) */
export const handleOperationalRefreshBlock = (responseBody) =>
  handleOperationalAccessBlock(responseBody, { fromRefresh: true });

export const cleanupLoginData = (useRedirect = false) => {
  // Read portal config BEFORE clearing session
  let portal = getSessionItem(STORAGE_KEYS.PORTAL_CONFIG);
  // Preserve operational block across sessionStorage.clear() so the block
  // screen can still read it if cleanup runs after a refresh block.
  const operationalBlock = getOperationalAuthBlock();
  const userId = getCurrentSession()?.userId;

  removeSessionItem(STORAGE_KEYS.AUTH_TOKEN);
  removeSessionItem(STORAGE_KEYS.AUTHORITY);
  removeSessionItem(STORAGE_KEYS.AUTH_USER);
  removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
  removeSessionItem(STORAGE_KEYS.AUTH_TOKEN_REFRESH);
  removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);

  // The keys above only cover the active namespace. Credentials surviving in
  // the user's other namespaces (portal/app) would be restored as a live
  // session on the next read and leave the tab on a context the shell cannot
  // serve, so drop them all while keeping non-auth data like the portal config.
  clearUserAuthTokens(userId);

  setCurrentSession(null);
  sessionStorage.clear();

  if (operationalBlock) {
    setOperationalAuthBlock(operationalBlock);
  }

  let loginUrl = '/user/login';

  const currentPath = history.location?.pathname;
  const isLoginUrl = currentPath === '/user/login';
  const isOperationalBlockUrl = currentPath === '/user/login/access-blocked';
  const queryParams = [];

  if (portal) {
    try {
      portal = JSON.parse(portal);
      queryParams.push(`portal=${portal.id}`);
    } catch (e) {}
  }

  const appMatch = currentPath?.match(/^\/apps\/([^/]+)/);
  if (appMatch) {
    queryParams.push(`app=${appMatch[1]}`);
    setGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT, appMatch[1]);
  }

  if (useRedirect && currentPath !== '/') {
    const redirect = isLoginUrl
      ? new URLSearchParams(window.location.search).get('redirect')
      : window.location.href;

    if (redirect) {
      queryParams.push(`redirect=${encodeURIComponent(redirect)}`);
    }
  }

  if (queryParams.length > 0) {
    loginUrl += `?${queryParams.join('&')}`;
  }

  // Do not bounce away from operational recovery screens to generic login.
  if (
    operationalBlock &&
    (isOperationalBlockUrl ||
      currentPath === '/support' ||
      currentPath?.startsWith('/billing/payment'))
  ) {
    return;
  }

  history.replace(loginUrl, history.location?.state || portal);
};

export const performLogout = async (useRedirect = false) => {
  // Store in raw sessionStorage — transient flag must survive session index clearing,
  // and cleanupLoginData's sessionStorage.clear() will naturally remove it.
  sessionStorage.setItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS, 'true');
  try {
    await authLogout();
  } finally {
    cleanupLoginData(useRedirect);
  }
};

/**
 * Safe logout function that checks logout flag to prevent concurrent logouts
 * @param {boolean} useRedirect - Whether to redirect after logout
 */
export const safeLogout = async (useRedirect = false) => {
  if (!sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS)) {
    try {
      await performLogout(useRedirect);
    } catch (e) {}
  }
};

export const handlePublicFragmentAuth = () => {
  const query = history.location?.query || {};
  const { publicCode, code } = query;

  const type = publicCode ? 'public' : 'external';
  const id = publicCode ?? md5(code);

  removeTokenById(type, id);
  window.location.reload();
};

export const handleTokenRefresh = async (token) => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Use the same lock name as the proactive path in token.js service so that both
  // mechanisms coordinate on a single named resource per session.
  // Multi-tab: tabs acquiring the same lock are serialized by the browser.
  const sessionId = getCurrentSessionId();
  const lockName = `token-refresh-${sessionId ?? 'default'}`;

  const run = async () => {
    if (isRefreshing && refreshPromise) return refreshPromise;
    isRefreshing = true;
    refreshPromise = performTokenRefresh(token);
    try {
      const result = await refreshPromise;
      if (result === REFRESH_RESULT.SUCCESS) {
        const { getQueueLength, processRequestQueue } = require('./requestQueue');
        if (getQueueLength() > 0) processRequestQueue();
      }
      return result;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  };

  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request(lockName, run);
  }
  return run();
};

const performTokenRefresh = async (token) => {
  try {
    const existingRefresh = getSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
    if (existingRefresh) {
      const refreshTime = parseInt(existingRefresh);
      if (Date.now() - refreshTime < 30_000) {
        // Another refresh is in flight — wait for it to complete before processing
        // the request queue. Returning true immediately causes the queue to replay
        // with the old token, resulting in a second wave of 401s.
        await new Promise((resolve) => {
          const interval = setInterval(() => {
            if (!getSessionItem(STORAGE_KEYS.AUTH_REFRESHING)) {
              clearInterval(interval);
              resolve();
            }
          }, 200);
          setTimeout(() => {
            clearInterval(interval);
            resolve();
          }, 30_000);
        });
        const freshToken = getToken();
        return !!freshToken?.accessToken;
      }
    }

    // Re-read the token from the current session right before calling the API.
    // The originally-passed `token` may be stale if a proactive refresh completed
    // between when handleAuthError read the token and when we reach this point.
    const freshToken = getToken() || token;

    // If the token was refreshed very recently (by the proactive worker path or
    // another tab), skip the redundant refresh — just confirm the token is valid.
    if (freshToken?.issuedAt && freshToken?.expiresIn) {
      const expiresAt = freshToken.issuedAt + freshToken.expiresIn * 1000;
      const timeUntilExpiry = expiresAt - Date.now();
      if (timeUntilExpiry > 5 * 60 * 1000 + 30_000) {
        return true;
      }
    }

    setSessionItem(STORAGE_KEYS.AUTH_REFRESHING, Date.now().toString());
    window.dispatch?.({ type: 'global/willRefreshToken' });

    const refreshedToken = await authRefreshToken(freshToken);
    removeSessionItem(STORAGE_KEYS.USER_CONTEXT);

    if (refreshedToken?.accessToken) {
      const user = jwtDecode(refreshedToken.accessToken);

      setSessionItem(
        STORAGE_KEYS.AUTH_TOKEN,
        JSON.stringify({ ...refreshedToken, issuedAt: Date.now() }),
      );

      setSessionItem(STORAGE_KEYS.AUTH_TOKEN_REFRESH, 'true');
      window.dispatch?.({ type: 'userContext/defaultContext', payload: user });
      setAuthority(Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles));
      // authRedirect(refreshedToken);
      // Sync token refresh with SocketService via token events
      try {
        if (refreshedToken.expiresIn) {
          const expiresAt = Date.now() + refreshedToken.expiresIn * 1000;
          await tokenManager.updateToken(
            getCurrentSessionId(),
            refreshedToken.accessToken,
            expiresAt,
          );
        }
        // Proactively request socket reconnection so new token is applied immediately
        tokenManager.requestSocketReconnect('token_refreshed');
      } catch (e) {}

      removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
      window.dispatch?.({ type: 'global/resetRefreshToken' });
      return REFRESH_RESULT.SUCCESS;
    }

    removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
    window.dispatch?.({ type: 'global/resetRefreshToken' });
    const { clearRequestQueue } = require('./requestQueue');
    clearRequestQueue(new Error('Token refresh failed'));
    return REFRESH_RESULT.FAILED;
  } catch (e) {
    removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
    window.dispatch?.({ type: 'global/resetRefreshToken' });

    const responseBody = await extractResponseBody(e);
    if (isOperationalRefreshBlockedResponse(responseBody)) {
      return handleOperationalRefreshBlock(responseBody);
    }

    const { clearRequestQueue } = require('./requestQueue');
    clearRequestQueue(e);
    return REFRESH_RESULT.FAILED;
  } finally {
    removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
  }
};

/**
 * Handle 401/403 authentication errors
 * @param {Response} response - HTTP response object
 * @param {AbortController} abortController - Abort controller
 */
export const handleAuthError = async (response, abortController) => {
  abortController.abort();
  const token = getToken();

  if (response.status === 403) {
    const responseBody = await extractResponseBody(response);
    const isAuthEndpoint = response.url?.includes('/auth');
    const isRefresh = response.url?.includes('/refreshToken');

    if (isRefresh) {
      if (isOperationalRefreshBlockedResponse(responseBody)) {
        await handleOperationalAccessBlock(responseBody, { fromRefresh: true });
        return;
      }
      // Invalid/expired refresh token — fall through to normal session cleanup.
      cleanupLoginData(true);
      return;
    }

    // Login / 2FA / OAuth / SSO: operational block with optional restrictedToken.
    if (isAuthEndpoint && isOperationalAccessBlockedResponse(responseBody)) {
      await handleOperationalAccessBlock(responseBody, { fromRefresh: false });
      return;
    }

    if (isOperationalRefreshBlockedResponse(responseBody)) {
      await handleOperationalAccessBlock(responseBody, { fromRefresh: true });
      return;
    }

    history.replace('/exception/403');
    return;
  }

  if (
    response.url.includes('/refreshToken') ||
    (response.status === 401 && response.url.includes('/logout'))
  ) {
    cleanupLoginData(true);
    return;
  }

  if (token) {
    if (isPublicFragment()) handlePublicFragmentAuth();
    else if (token.refreshToken) {
      notifyContext?.notify?.('tokenRefresh');
      const refreshResult = await handleTokenRefresh(token);
      if (refreshResult === REFRESH_RESULT.OPERATIONAL_BLOCK) return;
      if (!refreshResult) await performLogout(true);
    } else await performLogout(true);
  } else if (!isPublicFragment()) {
    // No usable token but the server still rejected us with 401. Without this
    // branch the app stays stuck on the 401 with no token to refresh and never
    // redirects. Clear any stale session remnants and send the user to login.
    cleanupLoginData(true);
  }
};

/**
 * Proactively ensure the current session token is still valid.
 *
 * Used by route guards and visibility handlers to close the "zombie session"
 * gap where an expired token sits in storage but no API call has triggered a
 * 401 yet — so the app keeps rendering as if logged in and never redirects.
 *
 * - Token valid (not expired): resolves true.
 * - Token expired with a refresh token: attempts a refresh; logs out on failure.
 * - Token expired without a refresh token: logs out.
 * - Operational block on refresh: resolves false without treating it as expired session.
 *
 * @returns {Promise<boolean>} true only when the session ends up valid.
 */
export const ensureFreshSession = async () => {
  // Public/external fragments use their own token lifecycle — leave them alone.
  if (isPublicFragment()) return true;
  if (sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS)) return false;
  if (operationalBlockHandling || getOperationalAuthBlock()) return false;

  const token = getToken();
  if (!token) return false;
  if (!isTokenExpired()) return true;

  if (token.refreshToken) {
    const refreshResult = await handleTokenRefresh(token);
    if (refreshResult === REFRESH_RESULT.OPERATIONAL_BLOCK) return false;
    if (!refreshResult) await safeLogout(true);
    return !!refreshResult;
  }

  await safeLogout(true);
  return false;
};
